/**
 * Poseidon operator dashboard — typed API contract.
 *
 * ============================================================================
 * STATUS: CONTRACT SKELETON. The Rust daemon does not serve these routes yet.
 * Every field below is the *proposed* wire shape that the Rust API owner must
 * implement (see README.md, "Contract the Rust API must expose"). Until it
 * exists, the only implementation of this contract is `src/mock/`, which is
 * synthetic and is labelled as such throughout the UI.
 * ============================================================================
 *
 * Design rules taken from the build spec:
 *
 * - §3.2 A  the ribbon and every KPI listed for the Live overview.
 * - §3.2 B  the complete decision trail for a trade, and the analytics charts.
 * - §3.2 C  explainable signal breakdown: quantitative contribution, *bounded*
 *           AI research adjustment, and risk constraints.
 * - §3.2 D  configured risk limits plus live utilization, and a read-only
 *           audit log of risk-config changes (operator, timestamp, prior value,
 *           new value, reason).
 * - §3.2 E  research assessments with evidence IDs, timestamps, schema
 *           validation result, score, confidence, counter-evidence, prompt and
 *           model versions, and call cost/latency.
 * - §6.4 / §19.2  the strict structured-assessment schema, including
 *           `reasoning_steps` and `conviction_tier`.
 * - §7.2    every risk rule result is persisted, including the rules that
 *           passed, so rejections can be explained in plain language.
 * - §8.3    the completed-trade ledger fields.
 * - §9.3    the required backtest and forward-test metrics.
 * - §11     account numbers arrive pre-masked; secrets never appear in payloads.
 *
 * Monetary values are integers-or-decimals in the account currency unless a
 * field name says otherwise, and are serialized as JSON numbers. Timestamps are
 * RFC 3339 / ISO 8601 UTC strings. Durations are milliseconds unless suffixed.
 */

export type IsoTimestamp = string;
export type Uuid = string;
/** A price, cash amount, lot size, ratio or score. JSON number; finite. */
export type Decimal = number;
/** sha256-prefixed content hash, e.g. `sha256:ab12…`. */
export type ContentHash = string;

/* ==========================================================================
 * §3.2 A — Status ribbon
 * ======================================================================== */

export type Mt5ConnectionState = 'connected' | 'disconnected';

/**
 * Mirrors `execution_mode` (§19.4). `approval_required` is the initial default
 * in both the spec and the UI.
 */
export type ExecutionMode = 'disabled' | 'approval_required' | 'demo_enabled';

/** §3.2 A / §7.3 session lifecycle. */
export type SessionPhase = 'pre_market' | 'active' | 'no_new_trades' | 'flattening' | 'closed';

/** §3.2 A / §7.3 risk posture. */
export type RiskState = 'normal' | 'warning' | 'soft_halted' | 'hard_halted';

export type DeepSeekHealth = 'healthy' | 'degraded' | 'unavailable';

/** §7.1: anything other than `demo` must fail closed. */
export type AccountMode = 'demo' | 'real' | 'contest' | 'unknown';
export type MarginMode = 'netting' | 'hedging';

export interface AccountBanner {
  mode: AccountMode;
  /** True only when the daemon has positively verified the account is a demo account. */
  demo_verified: boolean;
  broker_server: string;
  /**
   * Already masked by the daemon (§11: "Redact keys, account passwords, and
   * personally identifiable data"). The dashboard must never receive, store or
   * log the full account number.
   */
  account_number_masked: string;
  account_currency: string;
  margin_mode: MarginMode;
  leverage: Decimal;
  verified_at: IsoTimestamp;
}

export interface DataFreshness {
  tick_age_ms: number | null;
  bar_age_ms: number | null;
  /** Signed: positive means the broker/host clock is ahead of this dashboard. */
  clock_drift_ms: number | null;
  last_tick_at: IsoTimestamp | null;
  last_bar_at: IsoTimestamp | null;
  /** Newest domain event received over REST or WebSocket. Drives the staleness banner. */
  last_event_at: IsoTimestamp | null;
  /** Configured ceilings the ages above are judged against (§7.1 quote staleness). */
  thresholds: FreshnessThresholds;
}

export interface FreshnessThresholds {
  tick_aging_ms: number;
  tick_stale_ms: number;
  bar_aging_ms: number;
  bar_stale_ms: number;
  snapshot_aging_ms: number;
  snapshot_stale_ms: number;
  clock_drift_warn_ms: number;
  clock_drift_fail_ms: number;
}

export type HaltLevel = 'none' | 'soft' | 'hard';
export type HaltSource = 'operator' | 'automatic' | 'emergency_endpoint';

export interface HaltStateView {
  level: HaltLevel;
  activated_at: IsoTimestamp | null;
  activated_by: string | null;
  reason: string | null;
  source: HaltSource | null;
  /**
   * §7.3: a soft halt blocks new entries but still allows protective changes,
   * exits and cancels. A hard halt blocks everything except the explicit
   * emergency-close workflow. The UI renders these two states distinctly.
   */
  allows_protective_actions: boolean;
  allows_new_entries: boolean;
}

export type ReadinessOverall = 'ready' | 'degraded' | 'blocked';

/** The seven ribbon fields of §3.2 A, in spec order, as one push-able object. */
export interface RibbonStatus {
  as_of: IsoTimestamp;
  server_time: IsoTimestamp;
  mt5: Mt5ConnectionState;
  mt5_terminal: {
    terminal_build: string | null;
    last_heartbeat_at: IsoTimestamp | null;
    /** §4.5 reconciliation result summary. */
    last_reconciliation_at: IsoTimestamp | null;
    last_reconciliation_ok: boolean | null;
  };
  account: AccountBanner;
  execution_mode: ExecutionMode;
  session: {
    phase: SessionPhase;
    label: string;
    broker_timezone: string;
    broker_time: IsoTimestamp;
    next_cutoff_at: IsoTimestamp | null;
    flatten_deadline_at: IsoTimestamp | null;
  };
  risk_state: RiskState;
  halt: HaltStateView;
  data_freshness: DataFreshness;
  deepseek: {
    health: DeepSeekHealth;
    last_ok_at: IsoTimestamp | null;
    last_latency_ms: number | null;
    model_id: string | null;
    consecutive_failures: number;
  };
  readiness: ReadinessOverall;
  /** Non-blocking operator-facing alerts, newest first. Never used to hide state. */
  alerts: RibbonAlert[];
}

export interface RibbonAlert {
  id: string;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  raised_at: IsoTimestamp;
  source: string;
}

/* ==========================================================================
 * §3.2 A — Today's KPI cards
 * ======================================================================== */

/**
 * Exhaustive over the §3.2 A KPI list. Every metric the spec names has an
 * entry here; if the daemon has no value yet it sends a card with
 * `value: null` and `availability: 'unavailable'` rather than omitting it, so
 * a missing metric is visibly missing instead of silently absent.
 */
export type KpiId =
  | 'closed_trades_today'
  | 'open_positions'
  | 'win_rate'
  | 'wins_losses_breakeven'
  | 'realized_pnl'
  | 'unrealized_pnl'
  | 'net_pnl_today'
  | 'gross_profit'
  | 'gross_loss'
  | 'profit_factor'
  | 'expectancy_per_trade'
  | 'average_win'
  | 'average_loss'
  | 'largest_win'
  | 'largest_loss'
  | 'current_drawdown'
  | 'daily_loss_remaining'
  | 'orders_submitted'
  | 'orders_rejected'
  | 'average_fill_latency'
  | 'slippage';

export type KpiAvailability = 'ok' | 'unavailable' | 'stale';

/**
 * How the UI should render a value. Kept on the wire so the daemon controls
 * units (pips vs points, ms vs s) without a dashboard redeploy.
 */
export type KpiFormat = 'integer' | 'decimal' | 'percent' | 'currency' | 'ratio' | 'duration_ms' | 'pips' | 'count_pair';

/**
 * Visual tone for a KPI. `ok`/`positive` are both "good" but distinct: `ok`
 * means "within tolerance", `positive` means "contributing favourably".
 */
export type KpiTone = 'neutral' | 'ok' | 'positive' | 'negative' | 'warning' | 'critical' | 'muted';

export interface KpiCard {
  id: KpiId;
  label: string;
  value: Decimal | null;
  /** Pre-formatted for display; may be `—` when unavailable. */
  display: string;
  format: KpiFormat;
  tone: KpiTone;
  /** Secondary line, e.g. "of 3 configured" or "12 open · 84,200 notional". */
  sublabel: string | null;
  /** True when a smaller number is better (drawdown, slippage, rejections). */
  lower_is_better: boolean;
  availability: KpiAvailability;
  /** Plain-language explanation surfaced as a tooltip. */
  help: string | null;
  /** Populated when `availability !== 'ok'` and the UI must be honest about it. */
  unavailable_reason: string | null;
}

export interface KpiSnapshot {
  as_of: IsoTimestamp;
  /** Broker-local session date, `YYYY-MM-DD`. */
  session_date: string;
  account_currency: string;
  /** §3.3 `breakeven_threshold` actually used, so win rate is interpretable. */
  breakeven_threshold: Decimal;
  cards: KpiCard[];
}

/* ==========================================================================
 * §3.2 A / §8 — Live tables
 * ======================================================================== */

export type Side = 'buy' | 'sell';
export type PositionRiskStatus = 'protected' | 'unprotected_stop' | 'warning' | 'breach' | 'unknown';

/** §3.2 A open positions. */
export interface PositionRow {
  position_ticket: string;
  symbol: string;
  internal_instrument_id: string | null;
  side: Side;
  lots: Decimal;
  entry_price: Decimal;
  latest_bid: Decimal | null;
  latest_ask: Decimal | null;
  quote_age_ms: number | null;
  spread_points: Decimal | null;
  unrealized_pnl: Decimal;
  notional: Decimal;
  stop_loss: Decimal | null;
  take_profit: Decimal | null;
  /**
   * §16.2.4: a position is not protected until its stop is confirmed resting
   * server-side at the broker. `stop_loss != null` alone is not enough.
   */
  stop_confirmed_at_broker: boolean;
  opened_at: IsoTimestamp;
  age_ms: number;
  strategy_version: string;
  decision_id: Uuid;
  risk_status: PositionRiskStatus;
  magic_number: number;
}

export type WorkingOrderState =
  | 'pending_new'
  | 'placed'
  | 'partially_filled'
  | 'pending_cancel'
  | 'pending_modify'
  | 'rejected'
  | 'expired'
  | 'unknown';

/** §3.2 A working orders. */
export interface WorkingOrderRow {
  order_ticket: string;
  order_intent_id: Uuid | null;
  symbol: string;
  side: Side;
  lots: Decimal;
  requested_price: Decimal;
  order_state: WorkingOrderState;
  placed_at: IsoTimestamp;
  age_ms: number;
  /** Allowed price deviation in points (§4). */
  deviation_points: Decimal | null;
  filled_lots: Decimal;
  cancellable: boolean;
  /** Why cancellation is unavailable, when it is. */
  cancel_blocked_reason: string | null;
  magic_number: number;
}

/** §8.3 exit reasons. */
export type ExitReason = 'stop' | 'target' | 'time' | 'signal' | 'risk' | 'manual' | 'session_flatten';

/** §3.3 outcome buckets, computed after costs. */
export type TradeOutcome = 'win' | 'loss' | 'breakeven';

/** §3.2 A recent closed trades + the §8.3 completed-trade ledger fields. */
export interface ClosedTradeRow {
  completed_trade_id: string;
  decision_id: Uuid;
  strategy_version: string;
  symbol: string;
  internal_instrument_id: string | null;
  direction: Side;
  entry_timestamp: IsoTimestamp;
  average_entry_price: Decimal;
  entered_volume: Decimal;
  exit_timestamp: IsoTimestamp;
  average_exit_price: Decimal;
  exited_volume: Decimal;
  gross_realized_pnl: Decimal;
  commission: Decimal;
  swap: Decimal;
  fees: Decimal;
  /** gross − commission − swap − fees. Source of truth is broker deal history (§3.3). */
  net_realized_pnl: Decimal;
  initial_risk_cash: Decimal | null;
  r_multiple: Decimal | null;
  max_favorable_excursion: Decimal | null;
  max_adverse_excursion: Decimal | null;
  exit_reason: ExitReason;
  outcome: TradeOutcome;
  holding_time_ms: number;
  /** §8.3 ticket references, so a trade traces back to MT5 deals. */
  order_ticket: string | null;
  position_ticket: string | null;
  deal_tickets: string[];
  magic_number: number | null;
  research_confidence: Decimal | null;
  regime: RegimeLabel | null;
  tags: string[];
}

/** §3.2 A active signals. */
export type ResearchStatus = 'not_requested' | 'pending' | 'valid' | 'stale' | 'invalid' | 'unavailable';

export type RiskVerdict = 'approve' | 'reject' | 'reduce' | 'defer';

export type SignalLifecycleStatus =
  | 'candidate'
  | 'awaiting_research'
  | 'awaiting_risk'
  | 'awaiting_approval'
  | 'approved'
  | 'submitted'
  | 'executed'
  | 'rejected'
  | 'superseded'
  | 'expired';

export interface SignalRow {
  decision_id: Uuid;
  symbol: string;
  strategy_version: string;
  decision_time: IsoTimestamp;
  side: Side;
  entry_type: 'market' | 'marketable_limit' | 'limit' | 'stop';
  proposed_entry: Decimal;
  stop_loss: Decimal;
  take_profit: Decimal;
  time_exit_at: IsoTimestamp | null;
  /** Deterministic strategy score before any research adjustment (§5.3). */
  quant_score: Decimal;
  model_confidence: Decimal;
  /** base + research_adjustment + event_penalty, clamped (§6.5). */
  final_score: Decimal | null;
  research_status: ResearchStatus;
  risk_verdict: RiskVerdict | null;
  rejection_reason: string | null;
  status: SignalLifecycleStatus;
  regime: RegimeLabel | null;
  age_ms: number;
}

export type SystemEventKind =
  | 'mt5_connection'
  | 'data_quality_gate'
  | 'risk_event'
  | 'execution'
  | 'api_error'
  | 'manual_action'
  | 'reconciliation'
  | 'scheduler'
  | 'research'
  | 'config_change';

export type Severity = 'debug' | 'info' | 'warning' | 'error' | 'critical';

/** §3.2 A system events. */
export interface SystemEventRow {
  event_id: Uuid;
  occurred_at: IsoTimestamp;
  kind: SystemEventKind;
  severity: Severity;
  summary: string;
  detail: string | null;
  /** `operator:<name>`, `system:<component>`, or `mt5:ea`. */
  actor: string;
  correlation_id: Uuid | null;
  symbol: string | null;
  /** §11: payloads are redacted daemon-side; this flags that redaction happened. */
  redacted: boolean;
}

/* ==========================================================================
 * §3.2 B — Decision trail for one trade
 * ======================================================================== */

/** §18.2 deterministic regime classifier output. */
export type TrendBucket = 'trending' | 'ranging' | 'unknown';
export type VolBucket = 'low' | 'normal' | 'high' | 'unknown';
export type SessionPhaseBucket = 'opening' | 'mid_session' | 'pre_close' | 'unknown';

export interface RegimeSnapshotView {
  regime_version: string;
  symbol: string;
  bar_time: IsoTimestamp;
  trend_strength: TrendBucket;
  volatility_bucket: VolBucket;
  session_phase: SessionPhaseBucket;
  minutes_since_open: number;
  /** ADX/realized-vol inputs the buckets were derived from, for auditability. */
  inputs: { adx: Decimal | null; realized_vol_percentile: Decimal | null };
  features_hash: ContentHash;
}

/** Compact regime label used in tables. */
export interface RegimeLabel {
  trend_strength: TrendBucket;
  volatility_bucket: VolBucket;
  session_phase: SessionPhaseBucket;
  regime_version: string;
}

export type FeatureValueKind = 'number' | 'bool' | 'enum';

export interface FeatureValue {
  name: string;
  value: Decimal | boolean | string | null;
  kind: FeatureValueKind;
  unit: string | null;
  /** Deterministic contribution this feature made to the quantitative score. */
  contribution: Decimal | null;
}

export interface FeatureSnapshotView {
  feature_snapshot_id: string;
  feature_version: string;
  symbol: string;
  computed_at: IsoTimestamp;
  /** Immutable snapshot (§8.1): a re-run must reproduce this exact input set. */
  completed_bar_time: IsoTimestamp;
  values: FeatureValue[];
  features_hash: ContentHash;
}

/** §6.4 / §19.2 research assessment, as rendered in §3.2 E and the trail. */
export type CoverageStatus = 'sufficient' | 'partial' | 'insufficient' | 'stale' | 'unavailable';
export type EventDirection = 'bullish' | 'bearish' | 'neutral' | 'mixed';
export type RiskFlagSeverity = 'low' | 'medium' | 'high';
export type RiskFlagType =
  | 'scheduled_macro_event'
  | 'unscheduled_news'
  | 'liquidity_hole'
  | 'halt_or_limit'
  | 'corporate_action'
  | 'data_quality'
  | 'broker_condition'
  | 'other';

/**
 * §6.4: unknown risk flags and recommendations are rejected server-side. These
 * unions must match the Serde enums in `poseidon-research-engine`; the daemon
 * rejects anything outside them rather than coercing it.
 */
export type ResearchRecommendation = 'allow_quant_signal' | 'allow_reduced_size' | 'veto_quant_signal';

export type ConvictionTier = 'low' | 'medium' | 'high';

export interface ResearchRiskFlag {
  type: RiskFlagType;
  severity: RiskFlagSeverity;
  evidence_ids: string[];
  note: string | null;
}

export interface ResearchReasoningStep {
  claim: string;
  evidence_ids: string[];
}

/** §3.2 E schema-validation result. A failure is logged with its raw payload, never repaired (§6.4). */
export type SchemaValidationStatus = 'valid' | 'invalid' | 'rejected_unknown_field' | 'missing' | 'pending';

export interface SchemaValidationView {
  status: SchemaValidationStatus;
  schema_version: string | null;
  errors: string[];
  /** Reference to the stored raw payload. The dashboard must not inline raw LLM text as trusted. */
  raw_payload_ref: string | null;
  validated_at: IsoTimestamp | null;
}

export interface ResearchAssessmentView {
  assessment_id: Uuid;
  /** Null for pre-session research not attached to a signal candidate. */
  decision_id: Uuid | null;
  symbol: string;
  as_of: IsoTimestamp;
  received_at: IsoTimestamp;
  coverage_status: CoverageStatus;
  event_direction: EventDirection;
  event_relevance: Decimal;
  confidence: Decimal;
  event_horizon_minutes: number;
  risk_flags: ResearchRiskFlag[];
  /** §3.2 E: source evidence IDs, both supporting and counter. */
  evidence_ids: string[];
  counter_evidence_ids: string[];
  research_score: Decimal;
  recommendation: ResearchRecommendation;
  rationale: string;
  /** §19.2 bounded reasoning trace; each claim must cite supplied evidence. */
  reasoning_steps: ResearchReasoningStep[];
  conviction_tier: ConvictionTier | null;
  proposed_stop_adjustment_pct: Decimal | null;
  proposed_target_adjustment_pct: Decimal | null;
  schema_validation: SchemaValidationView;
  /** §3.2 E: prompt/model version and call cost/latency. */
  prompt_version: string;
  model_version: string;
  model_id: string;
  call: {
    llm_call_id: Uuid;
    started_at: IsoTimestamp;
    latency_ms: number;
    prompt_tokens: number;
    completion_tokens: number;
    cost_usd: Decimal;
    status: 'ok' | 'timeout' | 'rate_limited' | 'empty_response' | 'malformed_json' | 'schema_invalid' | 'error';
    attempt: number;
  };
  /**
   * §6.5: the *bounded* adjustment actually applied to the quantitative score,
   * after clamping to ±0.10 and after the event penalty. The raw `research_score`
   * is never what reaches the signal.
   */
  applied_adjustment: {
    research_adjustment: Decimal;
    event_penalty: Decimal;
    clamp_min: Decimal;
    clamp_max: Decimal;
    clamped: boolean;
    /** §19.3 fixed lookup table applied by the risk engine, not by the model. */
    conviction_multiplier: Decimal | null;
  };
  /** §6.4 staleness deadline; expired research is neutral or invalid. */
  staleness_deadline_at: IsoTimestamp | null;
  expired: boolean;
}

export interface RiskRuleResult {
  /** §7.2 evaluation order, 1-based, so rejection reasons are predictable. */
  order: number;
  rule_id: string;
  label: string;
  passed: boolean;
  verdict: RiskVerdict;
  /** Plain-language explanation, e.g. "EURUSD spread 2.4 pips exceeds configured 1.5-pip maximum." */
  explanation: string;
  observed_value: Decimal | string | null;
  configured_limit: Decimal | string | null;
  unit: string | null;
}

export interface RiskDecisionView {
  risk_decision_id: Uuid;
  decision_id: Uuid;
  decided_at: IsoTimestamp;
  verdict: RiskVerdict;
  /** §16.3.8: the risk config hash in force, so the decision stays replayable. */
  risk_config_hash: ContentHash;
  risk_config_version: string;
  /** Every rule, including the ones that passed (§7.2). */
  rules: RiskRuleResult[];
  approved_lots: Decimal | null;
  approved_entry: Decimal | null;
  approved_stop_loss: Decimal | null;
  approved_take_profit: Decimal | null;
  sizing: {
    risk_cash: Decimal;
    risk_per_lot: Decimal;
    raw_lots: Decimal;
    volume_step: Decimal;
    approved_lots: Decimal;
    conviction_multiplier: Decimal | null;
    strategy_allocation_share: Decimal | null;
  } | null;
}

export interface HumanApprovalView {
  approval_id: Uuid;
  decision_id: Uuid;
  /** `null` when the order path ran under `constrained_auto` (§19.4). */
  operator: string | null;
  decision: 'approved' | 'rejected' | 'expired' | 'auto_approved';
  decided_at: IsoTimestamp | null;
  reason: string | null;
  expires_at: IsoTimestamp | null;
  execution_mode_at_decision: ExecutionMode;
}

export interface Mt5OrderRequestView {
  order_intent_id: Uuid;
  decision_id: Uuid;
  /** §4.5 idempotency: the client-side key that must never produce a duplicate order. */
  idempotency_key: Uuid;
  submitted_at: IsoTimestamp | null;
  request: {
    symbol: string;
    side: Side;
    lots: Decimal;
    order_type: string;
    price: Decimal;
    stop_loss: Decimal;
    take_profit: Decimal;
    deviation_points: Decimal;
    magic_number: number;
    comment: string;
  };
  ea_acknowledged_at: IsoTimestamp | null;
  /** §4.2 `OrderCheck` outcome from the EA. */
  order_check: {
    performed_at: IsoTimestamp | null;
    retcode: number | null;
    retcode_label: string | null;
    margin_required: Decimal | null;
    free_margin_after: Decimal | null;
    advisory_only: boolean;
  } | null;
}

export interface BrokerOrderResultView {
  broker_order_id: Uuid;
  order_intent_id: Uuid;
  order_ticket: string | null;
  submitted_at: IsoTimestamp;
  final_state: WorkingOrderState;
  retcode: number | null;
  retcode_label: string | null;
  retcode_is_retryable: boolean | null;
  requested_price: Decimal;
  fill_price: Decimal | null;
  filled_lots: Decimal;
  fill_latency_ms: number | null;
  estimated_slippage_points: Decimal | null;
  actual_slippage_points: Decimal | null;
  rejection_reason: string | null;
}

export interface BrokerDealView {
  deal_ticket: string;
  broker_order_id: Uuid | null;
  position_ticket: string | null;
  executed_at: IsoTimestamp;
  symbol: string;
  side: Side;
  /** MT5 deal entry: 0 = in, 1 = out, 2 = in/out, 3 = out by. */
  entry: 'in' | 'out' | 'inout' | 'out_by';
  volume: Decimal;
  price: Decimal;
  commission: Decimal;
  swap: Decimal;
  fee: Decimal;
  profit: Decimal;
  comment: string | null;
  magic_number: number | null;
}

export interface PostTradeTagView {
  tag: string;
  applied_at: IsoTimestamp;
  applied_by: string;
  note: string | null;
}

/**
 * §3.2 B — the whole chain for one trade:
 * signal → feature snapshot → research evidence/response → risk rules →
 * human approval (if any) → MT5 order request/result → broker deals →
 * final net P&L and post-trade tags.
 *
 * Every step is optional-null rather than absent so the detail view can show
 * exactly which step is missing instead of silently skipping it.
 */
export interface TradeDecisionTrailView {
  completed_trade_id: string;
  decision_id: Uuid;
  loaded_at: IsoTimestamp;
  signal: SignalCandidateView | null;
  regime: RegimeSnapshotView | null;
  feature_snapshot: FeatureSnapshotView | null;
  research: ResearchAssessmentView | null;
  risk_decision: RiskDecisionView | null;
  human_approval: HumanApprovalView | null;
  order_request: Mt5OrderRequestView | null;
  order_results: BrokerOrderResultView[];
  broker_deals: BrokerDealView[];
  completed_trade: ClosedTradeRow | null;
  post_trade_tags: PostTradeTagView[];
  /** Aggregate execution quality, computed from the steps above. */
  execution_summary: {
    intent_to_fill_ms: number | null;
    estimated_slippage_points: Decimal | null;
    actual_slippage_points: Decimal | null;
    gross_realized_pnl: Decimal | null;
    total_costs: Decimal | null;
    net_realized_pnl: Decimal | null;
  };
}

/** §5.3 quantitative decision contract. */
export interface SignalCandidateView {
  decision_id: Uuid;
  strategy_version: string;
  symbol: string;
  decision_time: IsoTimestamp;
  side: Side;
  entry_type: 'market' | 'marketable_limit' | 'limit' | 'stop';
  proposed_entry: Decimal;
  stop_loss: Decimal;
  take_profit: Decimal;
  time_exit_at: IsoTimestamp | null;
  quant_score: Decimal;
  model_confidence: Decimal;
  features_hash: ContentHash;
  status: SignalLifecycleStatus;
  /** §18.4 arbitration bookkeeping when two strategies wanted the same symbol. */
  arbitration: {
    outcome: 'selected' | 'superseded' | 'conflict_rejected' | 'not_applicable';
    superseded_by: Uuid | null;
    note: string | null;
  };
}

/* ==========================================================================
 * §3.2 C — Explainable signal breakdown
 * ======================================================================== */

export type ScoreComponentKind = 'quantitative' | 'research_adjustment' | 'event_penalty' | 'risk_constraint';

export interface ScoreComponent {
  kind: ScoreComponentKind;
  label: string;
  /** Signed contribution to the final score. Risk constraints may be negative or a hard veto. */
  contribution: Decimal;
  detail: string;
  /** True when this component is an LLM-derived *adjustment*, never a standalone signal. */
  is_ai_derived: boolean;
  /** Set when the contribution was bounded by a configured clamp. */
  clamped_to: Decimal | null;
}

export interface RiskConstraintView {
  constraint_id: string;
  label: string;
  status: 'satisfied' | 'binding' | 'breached' | 'not_evaluated';
  explanation: string;
  observed_value: Decimal | null;
  limit_value: Decimal | null;
  unit: string | null;
}

export interface SignalBreakdownView {
  decision_id: Uuid;
  symbol: string;
  as_of: IsoTimestamp;
  /** Deterministic strategy score. The only component that can originate a trade. */
  base_score: Decimal;
  research_adjustment: Decimal;
  event_penalty: Decimal;
  final_score: Decimal;
  decision: 'trade' | 'no_trade';
  decision_reason: string;
  components: ScoreComponent[];
  risk_constraints: RiskConstraintView[];
  /** Configured thresholds the final score was compared against. */
  thresholds: { enter_above: Decimal; reduce_below: Decimal; configured_by: string };
  /** §6.5: research may only adjust a pre-existing quantitative signal. */
  research_can_originate_trade: false;
  /** Mandated §3.2 E labelling. Rendered verbatim by the UI. */
  ai_output_label: string;
}

/* ==========================================================================
 * §3.2 D — Risk center
 * ======================================================================== */

/** Exhaustive over the §3.2 D limit list. */
export type RiskLimitId =
  | 'daily_loss_limit'
  | 'per_trade_risk_limit'
  | 'max_open_positions'
  | 'max_gross_notional'
  | 'per_symbol_exposure_cap'
  | 'max_spread'
  | 'max_slippage'
  | 'max_consecutive_losses'
  | 'flatten_deadline'
  | 'halt_state';

export type RiskLimitUnit = 'currency' | 'percent_equity' | 'count' | 'points' | 'pips' | 'broker_time' | 'enum';

export type RiskLimitBreachAction = 'reject' | 'reduce' | 'defer' | 'soft_halt' | 'hard_halt' | 'stop_entries' | 'operator_review';

export type UtilizationTrend = 'rising' | 'falling' | 'flat';

export interface RiskLimitRow {
  limit_id: RiskLimitId;
  label: string;
  /** Categorised so the UI can group limits without hard-coding spec text. */
  category: 'loss' | 'size' | 'exposure' | 'execution' | 'behaviour' | 'session' | 'halt';
  unit: RiskLimitUnit;
  configured_value: Decimal | string;
  configured_display: string;
  /** Current consumption of the limit, in the same unit. Null when not measurable. */
  current_utilization: Decimal | null;
  utilization_display: string;
  /** 0.0–1.0+ fraction of the configured limit consumed. May exceed 1.0 when breached. */
  utilization_pct: Decimal | null;
  utilization_trend: UtilizationTrend | null;
  /** `warning` at the configured warn threshold, `breach` at/over the limit. */
  status: 'ok' | 'warning' | 'breach' | 'not_evaluated';
  breach_action: RiskLimitBreachAction;
  /** Hard ceiling that config drift may not exceed (e.g. §19.3 max conviction multiplier). */
  hard_ceiling: Decimal | null;
  /** §7.1: percentages are demo starting conditions, not universal constants. */
  editable: boolean;
  requires_second_confirmation: boolean;
  config_version: string;
  config_hash: ContentHash;
  last_modified_by: string | null;
  last_modified_at: IsoTimestamp | null;
  note: string | null;
}

/** §3.2 D read-only audit log for risk-configuration changes. */
export interface RiskConfigAuditRow {
  audit_event_id: Uuid;
  occurred_at: IsoTimestamp;
  /** Who made the change. Human operator, never "the system" for these rows. */
  operator: string;
  /** Which limit or allow-list entry changed. */
  target: string;
  target_kind: 'risk_limit' | 'allow_list' | 'execution_mode' | 'halt' | 'strategy_allocation';
  prior_value: string;
  new_value: string;
  /** Mandatory free-text justification. */
  reason: string;
  config_version_before: string;
  config_version_after: string;
  /** §16.3.3: a risk-config edit must be replayable against a historical session. */
  replay_validation: {
    required: boolean;
    performed: boolean;
    session_date: string | null;
    result: 'pass' | 'fail' | 'not_run' | null;
  };
  /** §11: hash-chained audit record. */
  chain_hash: ContentHash;
  previous_chain_hash: ContentHash | null;
  source_ip: string;
}

export interface AllowListEntryView {
  list_kind: 'instrument' | 'session_window' | 'strategy';
  entry_id: string;
  value: string;
  enabled: boolean;
  note: string | null;
  last_modified_by: string | null;
  last_modified_at: IsoTimestamp | null;
}

/* ==========================================================================
 * §3.2 E — Strategy & AI research
 * ======================================================================== */

/** §18.3 strategy registry and eligibility config. */
export interface StrategyEligibilityView {
  strategy_id: string;
  version: string;
  display_name: string;
  enabled: boolean;
  /** §5.1 intended regime / description. */
  intended_regime: string;
  entry_rules: string[];
  exit_rules: string[];
  eligibility: {
    session_phase: SessionPhaseBucket[];
    regime: TrendBucket[];
    volatility_bucket: VolBucket[];
    symbols: string[];
    /** §18.3 / §16.2.1 post-open warm-up, expressed as strategy eligibility. */
    min_minutes_after_open: number | null;
    max_spread_points: Decimal | null;
  };
  risk_allocation: {
    share_of_daily_budget: Decimal;
    /** §18.7: static, versioned, human-reviewed. Not adaptive. */
    policy: 'static_human_reviewed';
  };
  model_reference: { feature_version: string; model_version: string; config_hash: ContentHash };
  health: {
    trades: number;
    win_rate: Decimal | null;
    expectancy: Decimal | null;
    profit_factor: Decimal | null;
    max_drawdown: Decimal | null;
    average_r_multiple: Decimal | null;
    uptime_pct: Decimal | null;
    missed_trade_causes: { cause: string; count: number }[];
    last_signal_at: IsoTimestamp | null;
  };
  /** Live eligibility against the newest regime snapshot. */
  eligible_now: boolean;
  ineligible_reason: string | null;
  config_version: string;
}

export interface FeatureFreshnessRow {
  feature_version: string;
  symbol: string;
  feature_name: string;
  computed_at: IsoTimestamp;
  age_ms: number;
  expected_cadence_ms: number;
  status: 'fresh' | 'aging' | 'stale' | 'missing';
}

export interface ResearchCallCostSummary {
  window_start: IsoTimestamp;
  window_end: IsoTimestamp;
  calls: number;
  failures: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_cost_usd: Decimal;
  average_latency_ms: number | null;
  p95_latency_ms: number | null;
  schema_failure_rate: Decimal | null;
}

/* ==========================================================================
 * §3.2 F — Backtests and forward tests
 * ======================================================================== */

export type BacktestKind = 'historical_backtest' | 'tick_replay' | 'walk_forward' | 'paper_forward' | 'demo_forward';
export type BacktestStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';

/** §9.3 required backtest and forward-test metrics. */
export interface BacktestMetrics {
  completed_trades: number;
  win_rate: Decimal | null;
  loss_rate: Decimal | null;
  breakeven_rate: Decimal | null;
  profit_factor: Decimal | null;
  expectancy_per_trade: Decimal | null;
  average_r_multiple: Decimal | null;
  max_drawdown: Decimal | null;
  average_holding_time_ms: number | null;
  max_holding_time_ms: number | null;
  exposure_utilization: Decimal | null;
  margin_utilization: Decimal | null;
  turnover: Decimal | null;
  transaction_costs: Decimal | null;
  modeled_slippage_points: Decimal | null;
  actual_slippage_points: Decimal | null;
  strategy_uptime_pct: Decimal | null;
}

export interface BacktestEquityPoint {
  timestamp: IsoTimestamp;
  /** Equity if every position were closed at the current mark. */
  marked_equity: Decimal;
  /** Equity counting realized P&L only. */
  realized_equity: Decimal;
  drawdown: Decimal;
  drawdown_pct: Decimal;
}

export interface BacktestRunView {
  run_id: string;
  kind: BacktestKind;
  status: BacktestStatus;
  label: string;
  created_at: IsoTimestamp;
  started_at: IsoTimestamp | null;
  finished_at: IsoTimestamp | null;
  parameters: Record<string, string | number | boolean>;
  data_range: { start: IsoTimestamp; end: IsoTimestamp; timezone: string; provenance: string };
  /** §9.1 assumptions. Surfaced prominently so results are not read as guarantees. */
  assumptions: {
    spread_model: string;
    commission_model: string;
    swap_model: string;
    slippage_model: string;
    fill_model: string;
    intrabar_model: string | null;
  };
  /** §9.1 freeze list, extended by §18.6 to include the regime classifier and eligibility config. */
  versions: {
    strategy_version: string;
    feature_version: string;
    regime_version: string;
    eligibility_config_hash: ContentHash;
    model_version: string | null;
    cost_model_version: string;
    code_commit: string;
    config_hash: ContentHash;
  };
  metrics: BacktestMetrics;
  equity_curve: BacktestEquityPoint[];
  daily_drawdown_distribution: { bucket: string; count: number }[];
  trades_by_time_of_day: { bucket: string; count: number; net_pnl: Decimal }[];
  pnl_by_symbol: { key: string; net_pnl: Decimal; trades: number }[];
  pnl_by_regime: { key: string; net_pnl: Decimal; trades: number }[];
  /** §9.3 quant-only vs quant-plus-research comparison. */
  comparison: {
    mode: 'quant_only' | 'quant_plus_research';
    metrics: BacktestMetrics;
    /** §16.3.2 shadow A/B mode: both branches logged, only one executed. */
    executed_branch: boolean;
  }[];
  repro_artifact: { path: string; hash: ContentHash } | null;
  failure_reason: string | null;
  notes: string | null;
}

/* ==========================================================================
 * §3.2 G — Operations
 * ======================================================================== */

export interface BridgeStatusView {
  ea_name: string;
  protocol_version: string;
  authenticated: boolean;
  authentication_method: 'hmac_token' | 'none';
  /** Token itself is never sent to the browser — only whether auth is healthy. */
  token_present: boolean;
  last_authenticated_heartbeat_at: IsoTimestamp | null;
  heartbeat_interval_ms: number | null;
  missed_heartbeats: number;
  connected_since: IsoTimestamp | null;
  remote_address: string;
  sequence_number: number | null;
  last_reconciliation: {
    performed_at: IsoTimestamp | null;
    ok: boolean | null;
    positions_checked: number;
    orders_checked: number;
    mismatches: { kind: string; detail: string; ticket: string | null }[];
  };
}

export interface TerminalMetadataView {
  terminal_build: string | null;
  terminal_company: string | null;
  terminal_path_redacted: string | null;
  server_name: string;
  server_timezone: string;
  account_mode: AccountMode;
  margin_mode: MarginMode;
  trade_allowed: boolean | null;
  ea_trade_allowed: boolean | null;
  symbol_specs: {
    symbol: string;
    digits: number;
    point: Decimal;
    volume_min: Decimal;
    volume_step: Decimal;
    volume_max: Decimal;
    contract_size: Decimal;
    spread_typical_points: Decimal | null;
    swap_long: Decimal | null;
    swap_short: Decimal | null;
  }[];
}

export interface SchedulerView {
  scheduler_state: 'idle' | 'waiting' | 'running' | 'paused' | 'error';
  broker_timezone: string;
  broker_time: IsoTimestamp;
  session_open_at: IsoTimestamp | null;
  no_new_trades_at: IsoTimestamp | null;
  flatten_deadline_at: IsoTimestamp | null;
  session_close_at: IsoTimestamp | null;
  next_task: { name: string; at: IsoTimestamp } | null;
  upcoming: { name: string; at: IsoTimestamp; kind: 'session' | 'flatten' | 'report' | 'reconcile' }[];
  last_report_at: IsoTimestamp | null;
}

export interface DataHealthView {
  database: { reachable: boolean; latency_ms: number | null; migrations_applied: number | null; schema_version: string | null };
  tick_ingest: { last_tick_at: IsoTimestamp | null; ticks_last_minute: number; gaps_last_hour: number };
  bar_aggregation: { last_bar_at: IsoTimestamp | null; symbols_lagging: string[] };
  deepseek: { health: DeepSeekHealth; last_ok_at: IsoTimestamp | null };
  disk: { free_bytes: number | null; log_dir_bytes: number | null };
}

/** §16.3.7 pre-flight readiness check. */
export interface ReadinessCheckView {
  id: 'demo_mode' | 'bridge_auth' | 'database' | 'clock_drift' | 'deepseek_health' | 'session_state';
  label: string;
  status: 'pass' | 'warn' | 'fail' | 'unknown';
  detail: string;
  observed_at: IsoTimestamp;
  /** True when this check failing must prevent enabling execution. */
  blocking: boolean;
  remediation: string | null;
}

export interface ReadinessView {
  as_of: IsoTimestamp;
  overall: ReadinessOverall;
  /** §7.1 account type: anything but a verified demo account is a hard reject. */
  demo_mode: { pass: boolean; account_mode: AccountMode; demo_verified: boolean; detail: string };
  bridge_auth: { pass: boolean; authenticated: boolean; last_heartbeat_at: IsoTimestamp | null; detail: string };
  database: { pass: boolean; reachable: boolean; latency_ms: number | null; detail: string };
  clock_drift: { pass: boolean; drift_ms: number | null; warn_ms: number; fail_ms: number; detail: string };
  deepseek: { pass: boolean; health: DeepSeekHealth; detail: string };
  session_state: { pass: boolean; phase: SessionPhase; detail: string };
  checks: ReadinessCheckView[];
  /** Blocking failures, in operator-readable language. */
  blockers: string[];
  /** §3.1: true only when the daemon will accept command endpoints right now. */
  commands_enabled: boolean;
}

export interface AuthSessionView {
  authenticated: boolean;
  operator: string | null;
  /** §16.4.1 CSRF token; echoed back in the `x-poseidon-csrf` header on commands. */
  csrf_token: string | null;
  issued_at: IsoTimestamp | null;
  expires_at: IsoTimestamp | null;
  /** Demo-only workstation: execution remains blocked without a verified session. */
  role: 'operator' | 'viewer' | null;
}

/* ==========================================================================
 * §3.2 C — Market monitor
 * ======================================================================== */

export type Timeframe = 'M1' | 'M5' | 'M15';

/** `time` is the bar open time in UTC. */
export interface BarView {
  symbol: string;
  timeframe: Timeframe;
  time: IsoTimestamp;
  open: Decimal;
  high: Decimal;
  low: Decimal;
  close: Decimal;
  tick_volume: number;
  real_volume: number | null;
  spread_points: Decimal | null;
  /** False for the bar currently forming. §5.2: never signal on an unfinished bar. */
  complete: boolean;
}

export interface IndicatorSeriesView {
  symbol: string;
  timeframe: Timeframe;
  indicator: 'vwap' | 'atr14' | 'sma20' | 'ema50' | 'session_high' | 'session_low';
  version: string;
  points: { time: IsoTimestamp; value: Decimal }[];
}

export interface QuoteRowView {
  symbol: string;
  bid: Decimal;
  ask: Decimal;
  mid: Decimal;
  spread_points: Decimal;
  spread_pips: Decimal | null;
  quote_age_ms: number;
  last_tick_at: IsoTimestamp;
  session_volume: number | null;
  /** §7.1 quote staleness gate. */
  stale: boolean;
}

export interface StrategyMarkerView {
  marker_id: string;
  decision_id: Uuid;
  symbol: string;
  signal_time: IsoTimestamp;
  side: Side;
  proposed_entry: Decimal;
  stop_loss: Decimal;
  take_profit: Decimal;
  actual_fill_price: Decimal | null;
  actual_fill_time: IsoTimestamp | null;
  outcome: TradeOutcome | null;
  label: string;
}

/* ==========================================================================
 * §3.2 B — Analytics charts
 * ======================================================================== */

export type AnalyticsChartId =
  | 'cumulative_realized_pnl'
  | 'equity_curve'
  | 'win_loss_distribution'
  | 'pnl_by_hour'
  | 'pnl_by_weekday'
  | 'pnl_by_instrument'
  | 'pnl_by_strategy'
  | 'pnl_by_regime'
  | 'slippage_distribution'
  | 'holding_time_distribution'
  | 'consecutive_streaks'
  | 'session_trade_count_winrate';

/** Wide, defensive payload: charts pick the fields their shape needs. */
export interface ChartSeries {
  chart_id: AnalyticsChartId;
  title: string;
  subtitle: string | null;
  unit: string;
  /** Series keys must match the `x`/`y` keys referenced by the chart config. */
  series: { key: string; label: string; color_token: string }[];
  points: ChartPoint[];
  as_of: IsoTimestamp;
  /** Honest labelling when the window is partial. */
  sample_size: number;
  notes: string[];
}

export type ChartPoint = Record<string, string | number | null>;

export interface TradeFilters {
  from: IsoTimestamp | null;
  to: IsoTimestamp | null;
  sessions: SessionPhaseBucket[];
  strategies: string[];
  symbols: string[];
  outcomes: TradeOutcome[];
  exit_reasons: ExitReason[];
}

/* ==========================================================================
 * Commands (§3.1 safety) and WebSocket envelopes
 * ======================================================================== */

/** Every control listed in §3.1 that requires explicit typed confirmation. */
export type CommandKind =
  | 'set_execution_mode'
  | 'approve_order_intent'
  | 'reject_order_intent'
  | 'cancel_order'
  | 'close_position'
  | 'set_halt'
  | 'set_risk_limit'
  | 'set_allow_list';

export interface SetExecutionModePayload {
  mode: ExecutionMode;
}

export interface OrderIntentDecisionPayload {
  decision_id: Uuid;
  order_intent_id: Uuid | null;
  reason: string | null;
}

export interface CancelOrderPayload {
  order_ticket: string;
  symbol: string;
}

export interface ClosePositionPayload {
  position_ticket: string;
  symbol: string;
  lots: Decimal | null;
  /** Emergency-close workflow is the only path a hard halt still permits (§7.3). */
  emergency: boolean;
}

export interface SetHaltPayload {
  level: HaltLevel;
  reason: string;
  /** True when this is the separate emergency-close endpoint path, not the UI. */
  via_emergency_endpoint?: boolean;
}

export interface SetRiskLimitPayload {
  limit_id: RiskLimitId;
  new_value: Decimal | string;
  /** §3.2 D: mandatory justification, recorded in the audit log with prior/new value. */
  reason: string;
  /** §16.3.3: replay against a historical session before the change takes effect. */
  replay_session_date: string | null;
}

export interface SetAllowListPayload {
  list_kind: AllowListEntryView['list_kind'];
  entry_id: string;
  value: string;
  enabled: boolean;
  reason: string;
}

/**
 * A command envelope. The daemon must independently re-verify authority,
 * risk state and the confirmation phrase; the dashboard's dialog is a
 * safeguard for the operator, not a security control.
 */
export interface CommandRequest<TPayload = unknown> {
  command: CommandKind;
  payload: TPayload;
  /** The exact phrase the operator typed. Compared server-side; never trusted here. */
  confirmation: string;
  reason: string | null;
  idempotency_key: Uuid;
  requested_at: IsoTimestamp;
}

export interface CommandReceipt {
  command_id: Uuid;
  command: CommandKind;
  accepted: boolean;
  message: string;
  received_at: IsoTimestamp;
  audit_event_id: Uuid | null;
  /** State the daemon believes resulted, so the UI can reconcile immediately. */
  resulting_halt: HaltLevel | null;
  resulting_execution_mode: ExecutionMode | null;
  rejection_reason: string | null;
}

/* ==========================================================================
 * WebSocket — discriminated union of `DashboardEvent` messages
 * ======================================================================== */

export interface DashboardEventHeader {
  /** Envelope version. The UI refuses unknown versions rather than guessing. */
  v: 1;
  /** Monotonic per-connection sequence; a gap tells the UI to refetch. */
  seq: number;
  emitted_at: IsoTimestamp;
}

export type DashboardEvent =
  | (DashboardEventHeader & { type: 'heartbeat'; payload: { server_time: IsoTimestamp; data_freshness: DataFreshness } })
  | (DashboardEventHeader & { type: 'ribbon.updated'; payload: RibbonStatus })
  | (DashboardEventHeader & { type: 'kpi.snapshot'; payload: KpiSnapshot })
  | (DashboardEventHeader & { type: 'positions.snapshot'; payload: { items: PositionRow[] } })
  | (DashboardEventHeader & { type: 'position.updated'; payload: PositionRow })
  | (DashboardEventHeader & { type: 'position.closed'; payload: { position_ticket: string; completed_trade_id: string } })
  | (DashboardEventHeader & { type: 'orders.snapshot'; payload: { items: WorkingOrderRow[] } })
  | (DashboardEventHeader & { type: 'order.updated'; payload: WorkingOrderRow })
  | (DashboardEventHeader & { type: 'trade.closed'; payload: ClosedTradeRow })
  | (DashboardEventHeader & { type: 'signal.new'; payload: SignalRow })
  | (DashboardEventHeader & { type: 'signal.updated'; payload: SignalRow })
  | (DashboardEventHeader & { type: 'signal.breakdown'; payload: SignalBreakdownView })
  | (DashboardEventHeader & { type: 'research.assessment'; payload: ResearchAssessmentView })
  | (DashboardEventHeader & { type: 'regime.updated'; payload: RegimeSnapshotView })
  | (DashboardEventHeader & { type: 'risk.limits'; payload: { items: RiskLimitRow[]; halt: HaltStateView } })
  | (DashboardEventHeader & { type: 'risk.audit'; payload: RiskConfigAuditRow })
  | (DashboardEventHeader & { type: 'system.event'; payload: SystemEventRow })
  | (DashboardEventHeader & { type: 'readiness.updated'; payload: ReadinessView })
  | (DashboardEventHeader & { type: 'bridge.updated'; payload: BridgeStatusView })
  | (DashboardEventHeader & { type: 'quote.updated'; payload: QuoteRowView })
  | (DashboardEventHeader & { type: 'backtest.updated'; payload: { run_id: string; status: BacktestStatus; progress_pct: number | null } })
  | (DashboardEventHeader & { type: 'session.updated'; payload: { phase: SessionPhase; broker_time: IsoTimestamp; next_cutoff_at: IsoTimestamp | null } })
  | (DashboardEventHeader & {
      type: 'error';
      payload: { code: string; message: string; retryable: boolean; correlation_id: Uuid | null };
    });

export type DashboardEventType = DashboardEvent['type'];

/**
 * The UI rejects any frame whose `type` is not in this list. An unknown event
 * means the daemon is newer than the served bundle — the dashboard surfaces a
 * version-mismatch banner instead of silently dropping the message.
 */
export const DASHBOARD_EVENT_TYPES = [
  'heartbeat',
  'ribbon.updated',
  'kpi.snapshot',
  'positions.snapshot',
  'position.updated',
  'position.closed',
  'orders.snapshot',
  'order.updated',
  'trade.closed',
  'signal.new',
  'signal.updated',
  'signal.breakdown',
  'research.assessment',
  'regime.updated',
  'risk.limits',
  'risk.audit',
  'system.event',
  'readiness.updated',
  'bridge.updated',
  'quote.updated',
  'backtest.updated',
  'session.updated',
  'error',
] as const satisfies readonly DashboardEventType[];

/* ==========================================================================
 * Transport envelopes
 * ======================================================================== */

export type DataSourceMode = 'mock' | 'live';

/** Every REST response is wrapped so `as_of` and provenance travel with the data. */
export interface ApiEnvelope<T> {
  data: T;
  /** When the daemon computed this payload. Drives the staleness indicator. */
  as_of: IsoTimestamp;
  server_time: IsoTimestamp;
  /** §7: `synthetic` means mock/placeholder values. The UI badges this loudly. */
  provenance: 'live' | 'replay' | 'synthetic';
  /** Present when the payload is degraded but usable (e.g. partial history). */
  warnings: string[];
}

export interface Paged<T> {
  items: T[];
  total: number;
  next_cursor: string | null;
}

export interface ApiErrorBody {
  code: string;
  message: string;
  /** Field-level detail for validation failures on commands. */
  fields: { field: string; message: string }[];
  correlation_id: Uuid | null;
  retryable: boolean;
}
